import React, { useEffect, useState } from "react";
import useResponsive from "./useResponsive";
import { getUser } from "./utils";

const TUTORIAL_STORAGE_KEYS = {
  completed: "app_tutorial_completed_v1",
  progress: "app_tutorial_current_step",
  selectedAreas: "app_tutorial_selected_areas",
  // Keep in sync with TUTORIAL_STORAGE_KEYS in src/components/Tutorial/types.ts
  briefCompleted: "brief_tour_completed_v1",
};

const SIDEBAR_EXPANDED_STORAGE_KEY = "sidebar_expanded";

export const AppContext = React.createContext({});

export function AppProvider({ children }) {
  const { isMobile } = useResponsive();
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    // Lazy init from localStorage so the user's collapsed/expanded preference
    // survives reloads. The `sidebar_expanded` key is also preserved across
    // logout by the Sidebar component's handleLogout.
    if (typeof window === "undefined") return false;
    return localStorage.getItem(SIDEBAR_EXPANDED_STORAGE_KEY) === "true";
  });

  useEffect(() => {
    localStorage.setItem(SIDEBAR_EXPANDED_STORAGE_KEY, sidebarExpanded ? "true" : "false");
  }, [sidebarExpanded]);
  const [isChatBotOpen, setIsChatBotOpen] = useState(false);
  // Draft message handed to the chatbot's "Contact Support" ticket modal when
  // support is opened from elsewhere (e.g. the Google API outage callout). Null
  // when there's nothing to prefill; the chatbot clears it once consumed.
  const [supportPrefill, setSupportPrefill] = useState(null);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [isMarketingNotificationPanelOpen, setIsMarketingNotificationPanelOpen] = useState(false);
  const [marketingNotificationInitialTab, setMarketingNotificationInitialTab] = useState("activity");
  const [userProfileImg, setUserProfileImg] = useState(getUser()?.profileImg || null);

  // dashboard data
  const [loading, setLoading] = useState(false);

  // Tutorial state
  const [tutorialActive, setTutorialActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [tourMode, setTourMode] = useState<"brief" | "detailed">("detailed");
  const [showAreaSelection, setShowAreaSelection] = useState(false);
  const [hasCompletedBefore, setHasCompletedBefore] = useState(() => {
    return localStorage.getItem(TUTORIAL_STORAGE_KEYS.completed) === "true";
  });
  // Request channel: a tutorial step asks ChatV2Production to switch chat
  // tabs (e.g. open the pricing agent on the Agent tab). Null when idle;
  // ChatV2Production clears it once consumed.
  const [tourChatModeRequest, setTourChatModeRequest] = useState<"chat" | "agent" | null>(null);

  const onBarOpen = () => {
    // on any bar open, chatbot or sidebar, add a class to the body called "sidebar-open"
    document.body.classList.add("sidebar-open");
  };

  useEffect(() => {
    if (isChatBotOpen) {
      onBarOpen();
      setIsNotificationPanelOpen(false);
      setIsMarketingNotificationPanelOpen(false);
    }
  }, [isChatBotOpen]);

  useEffect(() => {
    if (sidebarExpanded) {
      onBarOpen();
      setIsNotificationPanelOpen(false);
      setIsMarketingNotificationPanelOpen(false);
    }
  }, [sidebarExpanded]);

  useEffect(() => {
    if (isNotificationPanelOpen) {
      setIsChatBotOpen(false);
      setIsMarketingNotificationPanelOpen(false);
    }
  }, [isNotificationPanelOpen]);

  useEffect(() => {
    if (isMarketingNotificationPanelOpen) {
      setIsChatBotOpen(false);
      setIsNotificationPanelOpen(false);
    }
  }, [isMarketingNotificationPanelOpen]);

  useEffect(() => {
    if (!isChatBotOpen && !sidebarExpanded) {
      document.body.classList.remove("sidebar-open");
    } else {
      onBarOpen();
    }
  }, [isChatBotOpen, sidebarExpanded]);

  // Tutorial management methods
  const startTutorial = (areas) => {
    if (!areas || areas.length === 0) {
      console.warn("Cannot start tutorial without selected areas");
      return;
    }

    setTourMode("detailed");
    setSelectedAreas(areas);
    setCurrentStepIndex(0);
    setTutorialActive(true);
    setShowAreaSelection(false);

    // Save to localStorage
    localStorage.setItem(TUTORIAL_STORAGE_KEYS.selectedAreas, JSON.stringify(areas));
    localStorage.setItem(TUTORIAL_STORAGE_KEYS.progress, "0");
  };

  const startBriefTour = () => {
    setTourMode("brief");
    setSelectedAreas([]);
    setCurrentStepIndex(0);
    setTutorialActive(true);
    setShowAreaSelection(false);
    localStorage.setItem(TUTORIAL_STORAGE_KEYS.progress, "0");
  };

  const nextStep = (totalSteps) => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex >= totalSteps) {
      // Tutorial completed
      completeTutorial();
    } else {
      setCurrentStepIndex(nextIndex);
      localStorage.setItem(TUTORIAL_STORAGE_KEYS.progress, nextIndex.toString());
    }
  };

  const previousStep = () => {
    if (currentStepIndex > 0) {
      const prevIndex = currentStepIndex - 1;
      setCurrentStepIndex(prevIndex);
      localStorage.setItem(TUTORIAL_STORAGE_KEYS.progress, prevIndex.toString());
    }
  };

  const skipStep = (totalSteps) => {
    // Skip is the same as next in our implementation
    nextStep(totalSteps);
  };

  const exitTutorial = () => {
    setTutorialActive(false);
    setCurrentStepIndex(0);
    setSelectedAreas([]);

    // Clear progress from localStorage
    localStorage.removeItem(TUTORIAL_STORAGE_KEYS.progress);
    localStorage.removeItem(TUTORIAL_STORAGE_KEYS.selectedAreas);
  };

  const completeTutorial = () => {
    setTutorialActive(false);
    setCurrentStepIndex(0);

    // Mark as completed in localStorage
    if (tourMode === "brief") {
      localStorage.setItem(TUTORIAL_STORAGE_KEYS.briefCompleted, "true");
    } else {
      setHasCompletedBefore(true);
      localStorage.setItem(TUTORIAL_STORAGE_KEYS.completed, "true");
    }
    localStorage.removeItem(TUTORIAL_STORAGE_KEYS.progress);
    localStorage.removeItem(TUTORIAL_STORAGE_KEYS.selectedAreas);
  };

  const openAreaSelection = () => {
    setShowAreaSelection(true);
  };

  const closeAreaSelection = () => {
    setShowAreaSelection(false);
  };

  const openSupportTicket = (message) => {
    setSupportPrefill(message);
    setIsChatBotOpen(true);
  };

  const values = {
    loading,
    setLoading,
    setSidebarExpanded,
    sidebarExpanded,
    isChatBotOpen,
    setIsChatBotOpen,
    supportPrefill,
    setSupportPrefill,
    openSupportTicket,
    setIsMobileSidebarOpen,
    isMobileSidebarOpen,
    userProfileImg,
    setUserProfileImg,
    isNotificationPanelOpen,
    setIsNotificationPanelOpen,
    isMarketingNotificationPanelOpen,
    setIsMarketingNotificationPanelOpen,
    marketingNotificationInitialTab,
    setMarketingNotificationInitialTab,
    // Tutorial
    tutorialActive,
    currentStepIndex,
    selectedAreas,
    showAreaSelection,
    hasCompletedBefore,
    tourMode,
    tourChatModeRequest,
    setTourChatModeRequest,
    startTutorial,
    startBriefTour,
    nextStep,
    previousStep,
    skipStep,
    exitTutorial,
    openAreaSelection,
    closeAreaSelection,
  };

  return <AppContext.Provider value={values}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = React.useContext(AppContext);
  if (context === undefined) {
    throw new Error("useApp must be used within a AppProvider");
  }
  return context;
}
